import { attachedFreeInspectionSchema, correctSchematicSchema, freeInspectionListSchema, inquirySchema, mediumLinkSchema, submittedInspectionsSchema, } from '@ghp/domain';
import { bearer } from './http.js';
/**
 * Le contrôle transmis (D4, D21) : sa lecture — le relevé et les pièces qu'il référence — et les
 * deux décisions du bureau.
 *
 * ⚠️ Tous les appels portent la **demande**, jamais le seul média ni la seule proposition. Ce
 * n'est pas la forme la plus courte : pour la lecture d'une pièce, c'est ce qui permet au serveur
 * de retrouver l'équipe, donc la clé de stockage, et d'interdire qu'un identifiant deviné atteigne
 * la pièce d'une autre équipe (`apps/api/src/validation/validation.service.ts`) ; pour les deux
 * décisions, c'est l'identifiant que l'écran de validation détient.
 */
export function createValidationEndpoints(http) {
    return {
        /**
         * Toutes les propositions de la demande, la plus récente d'abord.
         *
         * ⚠️ `total` compte celles que la demande porte, `items` celles qui ont pu être relues : leur
         * écart est ce qui permet à l'écran de dire qu'il manque une fiche. Ne lire qu'`items.length`
         * est l'erreur que ce contrat existe pour empêcher.
         */
        readInspections(accessToken, inquiryId) {
            return http.get(`/validation/inquiries/${inquiryId}/inspections`, submittedInspectionsSchema, bearer(accessToken));
        },
        /**
         * Les octets d'une pièce — photo, vidéo, PDF ou signature manuscrite.
         *
         * ⚠️ Rend un `Blob`, à l'appelant d'en faire une URL d'objet **et de la révoquer** : le jeton
         * vit en mémoire (D3), donc `<img src>` ne peut pas s'authentifier tout seul. Voir
         * `http.getBinary`, qui porte le raisonnement.
         */
        readMedium(accessToken, inquiryId, mediaId) {
            return http.getBinary(`/validation/inquiries/${inquiryId}/media/${mediaId}`, bearer(accessToken));
        },
        /**
         * L'URL de lecture signée d'une pièce, pour quinze minutes (#418) — `null` quand le stockage
         * n'en signe pas (mode disque) : relire alors les octets par `readMedium`.
         */
        mediumLink(accessToken, inquiryId, mediaId) {
            return http.get(`/validation/inquiries/${inquiryId}/media/${mediaId}/link`, mediumLinkSchema, bearer(accessToken));
        },
        /**
         * Les octets du **rapport PDF** du dernier contrôle validé de la demande (M11).
         *
         * ⚠️ `404` tant qu'il n'est pas généré — la génération suit la validation en file de jobs. La
         * lecture le dit d'avance (`report: null`) : un écran qui propose le bouton seulement quand
         * `report` est renseigné n'a pas à tenter sa chance. Le nom du fichier se compose côté client
         * (`reportFileName`), le même que celui que l'API pose dans `Content-Disposition`.
         */
        readReport(accessToken, inquiryId) {
            return http.getBinary(`/validation/inquiries/${inquiryId}/report`, bearer(accessToken));
        },
        /**
         * **Relance** la génération du rapport du dernier contrôle validé — le geste que la lecture
         * arme quand `reportStalled` dit qu'aucune génération n'est en file ni en cours.
         *
         * `202` sans corps : le rapport viendra par la file, et l'écran le voit arriver en relisant la
         * fiche. Idempotent côté serveur — un rapport déjà là ou une génération encore en file ne
         * valent pas un refus. Échecs en `ApiError` : `404` sur une demande sans contrôle validé, et
         * une file indisponible, puisque publier est tout l'objet du geste.
         */
        retryReport(accessToken, inquiryId) {
            return http.postEmpty(`/validation/inquiries/${inquiryId}/report`, bearer(accessToken));
        },
        /**
         * L'**aperçu** du rapport d'un contrôle **pas encore validé** — « Fiche PDF » avant « Marquer
         * « vérifié » », filigrane « Brouillon » compris quand le réglage le demande. Composé à la
         * demande depuis la dernière saisie transmise : ce n'est pas le livrable, rien n'est écrit.
         *
         * À appeler tant que la lecture rend `report: null` et qu'une saisie existe ; une fois le
         * contrôle validé, c'est `readReport` qui sert le livrable. Échecs en `ApiError` : `404` sans
         * saisie transmise, `409` sur un contrôle validé ou une saisie illisible
         * (`proposal_unreadable`) — le `message` est affichable tel quel.
         */
        previewReport(accessToken, inquiryId) {
            return http.getBinary(`/validation/inquiries/${inquiryId}/report/preview`, bearer(accessToken));
        },
        /**
         * Valide le contrôle : acte la décision et passe la demande en `validated`.
         *
         * ⚠️ `proposalId` n'est pas de la cérémonie : c'est le **jeton de concurrence optimiste** de la
         * paire lecture/écriture. Passer celui de la fiche affichée (`read.items[0].id`) et rien
         * d'autre — sans lui, une saisie arrivée depuis la lecture serait validée à la place, et le
         * valideur signerait une fiche qu'il n'a pas vue.
         *
         * ⚠️ **La demande rendue est celle que le serveur a écrite**, statut compris : c'est ce qui dit
         * à l'écran qu'il doit changer de file. Réafficher ce qu'on a envoyé mentirait.
         *
         * Un échec sort en `ApiError` : `409` avec un corps conforme à `validationConflictSchema` —
         * `code` dit lequel des refus (`validationConflictCodeSchema`), `message` est affichable tel
         * quel — et un `409` signifie
         * **toujours** que rien n'a été écrit.
         */
        /**
         * Le schéma corrigé au bureau avant la validation (#437) : le dessin et l'image JPEG que
         * l'éditeur a rendue. `409` sur un contrôle qui n'est plus transmis (M11).
         */
        correctSchematic(accessToken, inquiryId, correction, image) {
            const form = new FormData();
            form.append('image', image, 'schema.jpg');
            form.append('correction', JSON.stringify(correction));
            return http.postFormJson(`/validation/inquiries/${inquiryId}/schematic`, form, correctSchematicSchema, bearer(accessToken));
        },
        validate(accessToken, inquiryId, request) {
            return http.postJson(`/validation/inquiries/${inquiryId}/validate`, request, inquirySchema, bearer(accessToken));
        },
        /**
         * Renvoie le contrôle au terrain pour correction : la demande repasse en `in_progress` et
         * **réapparaît d'elle-même dans la tournée de l'équipe**.
         *
         * `reason` est obligatoire et non blanc : c'est la consigne que l'agent lira. Le seul `409`
         * possible ici est `transition_not_suggested` — renvoyer est le geste de rattrapage, il
         * n'exige ni la mention du signataire ni la proposition la plus récente.
         */
        reject(accessToken, inquiryId, request) {
            return http.postJson(`/validation/inquiries/${inquiryId}/reject`, request, inquirySchema, bearer(accessToken));
        },
        /** La file « Contrôles libres » : faits sur place sans demande, à rattacher (M41). */
        listFreeInspections(accessToken) {
            return http.get('/validation/free-inspections', freeInspectionListSchema, bearer(accessToken));
        },
        /** Une pièce d'un contrôle libre, résolue par sa proposition comme `readMedium`. */
        readFreeMedium(accessToken, proposalId, mediaId) {
            return http.getBinary(`/validation/free-inspections/${proposalId}/media/${mediaId}`, bearer(accessToken));
        },
        attachFreeInspection(accessToken, proposalId, request) {
            return http.postJson(`/validation/free-inspections/${proposalId}/attach`, request, attachedFreeInspectionSchema, bearer(accessToken));
        },
        async dismissFreeInspection(accessToken, proposalId, request) {
            await http.post(`/validation/free-inspections/${proposalId}/dismiss`, request, bearer(accessToken));
        },
    };
}
//# sourceMappingURL=validation.js.map